import { defineStore } from 'pinia';
import checkFF from '@webapp-next/lib/feature-flag';
import getPayrollStatus from '@apps-payroll/lib/request/svc/payroll/get-payroll-status';
import { isUKProvider } from '@apps-payroll/utils/check-providers';

interface UkPayrollGateState {
  isUkPayroll: boolean;
  request: Promise<void> | null;
}

// The server-side LaunchDarkly flag that switches the backend Payroll ID checks on. The client
// rules follow it, so a tenant the backend does not check sees no client rules either.
export const PAYROLL_ID_VALIDATION_FLAG = 'payk-306-uk-payroll-id-validation';

/**
 * True when the PAYROLL_ID_VALIDATION_FLAG flag is on. A server refusal of a Payroll ID is shown
 * on the field only when it is, because the backend sends one only then.
 */
export function isPayrollIdValidationFlagOn(): boolean {
  return checkFF(PAYROLL_ID_VALIDATION_FLAG) === true;
}

/**
 * Holds the answer to "is this tenant onboarded to UK payroll (IRIS)?" for the whole page,
 * so every form shares one request. A new Pinia instance starts with no answer; `$reset()`
 * forgets it. See use-uk-payroll-gate.ts for the rules.
 */
const useUkPayrollGateStore = defineStore('webapp:people:uk-payroll-gate', {
  state: (): UkPayrollGateState => ({
    isUkPayroll: false,
    request: null,
  }),
  actions: {
    load(): Promise<void> {
      if (!this.request) {
        this.request = Promise.resolve()
          .then(() => getPayrollStatus())
          .then(response => {
            const onboarded = response?.data?.onboarded === true;
            const provider = response?.data?.provider || '';
            this.isUkPayroll = onboarded && isUKProvider(provider);
          })
          .catch(e => {
            console.error('Payroll gate failed:', e);
            this.isUkPayroll = false;
            // The settled request is kept, so a failure is logged once and not retried while
            // svc-payroll is down. The page answers false until it is reloaded.
          });
      }
      return this.request;
    },
  },
});

/**
 * The one place the Payroll ID rules are switched on: the PAYROLL_ID_VALIDATION_FLAG flag is on
 * and the tenant is onboarded to UK payroll. All Payroll ID code reads this. Code that only needs
 * "onboarded" (the date of birth and address checks) reads `isUkPayroll` instead.
 */
export function isPayrollIdValidationActive(
  store: ReturnType<typeof useUkPayrollGateStore> = useUkPayrollGateStore()
): boolean {
  return isPayrollIdValidationFlagOn() && store.isUkPayroll;
}

export default useUkPayrollGateStore;
